
import { v4 as uuidv4 } from 'uuid';
import { trackOrderEvent, determineTrackingPlatforms } from '@/lib/api/trackingService';
import { getCookieValue, getURLParameter, getOrCreateExternalId } from './utils';
import { PageViewEventData } from './types';

/**
 * Sends a PageView event to the tracking service
 */
export const sendPageViewToTrackingService = async (
  organizationId: string,
  shopId: string | null,
  eventData: PageViewEventData
): Promise<boolean> => {
  try {
    // Generate a unique event ID
    const eventId = uuidv4();
    
    // Get tracking identifiers - ensure proper cookie collection
    const fbp = getCookieValue('_fbp') || '';
    const fbc = getCookieValue('_fbc') || getURLParameter('fbclid') || '';
    const externalId = getOrCreateExternalId();
    
    // Determine which platforms to send this event to
    const platforms = determineTrackingPlatforms('PageView');
    
    console.log(`📊 Sending PageView to tracking service for path: ${eventData.page_path}, shop: ${shopId || 'unknown'}, org: ${organizationId}, attempt: ${eventData.attempt || 1}`);
    console.log(`🍪 Facebook cookies - fbp: ${fbp ? 'present' : 'missing'}, fbc: ${fbc ? 'present' : 'missing'}`);
    
    // Get user agent and additional browser information
    const userAgent = navigator.userAgent;
    const country = navigator.language.split('-')[1]?.toLowerCase() || 'unknown';
    
    // Format event data in a way that matches Meta's API expectations
    const enhancedEventData = {
      ...eventData,
      // Standard Meta API fields
      content_name: document.title,
      content_category: 'page',
      content_type: 'product',
      
      // User and browser data for user_data field
      country,
      external_id: externalId,
      client_user_agent: userAgent,
      fbp_cookie: fbp || undefined,
      fbc_cookie: fbc || undefined,
      
      // Diagnostic data
      timestamp_ms: Date.now(),
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      event_source_url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      cookie_enabled: typeof navigator !== 'undefined' ? navigator.cookieEnabled : 'unknown'
    };
    
    // Send the event to the tracking service with detailed debugging info
    const result = await trackOrderEvent({
      organization_id: organizationId,
      shop_id: shopId || undefined,
      event_type: 'PageView',
      event_id: eventId,
      external_id: externalId,
      fbc: fbc || undefined,
      fbp: fbp || undefined,
      custom_data: enhancedEventData,
      platforms
    });
    
    if (result) {
      console.log('✅ PageView event sent successfully to server');
      // If this is the first attempt and it succeeds, mark it as sent
      if (!eventData.attempt || eventData.attempt <= 1) {
        if (typeof window !== 'undefined') {
          window._pageViewSent = window._pageViewSent || {};
          window._pageViewSent[eventData.page_path] = true;
          
          // Dispatch a custom event to notify other components that PageView has been sent
          window.dispatchEvent(new CustomEvent('pageViewSent', { 
            detail: { 
              pathname: eventData.page_path,
              timestamp: new Date().toISOString()
            } 
          }));
        }
      }
    } else {
      console.warn('⚠️ PageView event failed to send to server');
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error tracking PageView:', error);
    return false;
  }
};
